import React, { useEffect, useState } from "react";
import { PencilSimple, Trash, Plus, X } from "@phosphor-icons/react";
import { api, fileUrl, serviceImage } from "../../lib/api";
import ImageUploader from "../../components/admin/ImageUploader";
import VideoUrlsInput from "../../components/admin/VideoUrlsInput";
import { toast } from "sonner";

const EMPTY = { title: "", short_description: "", long_description: "", features: [], image_path: "", video_urls: [], featured: true, order: 0 };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [featureInput, setFeatureInput] = useState("");

  const load = () => api.get("/services").then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); setFeatureInput(""); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...EMPTY, ...s }); setFeatureInput(""); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/services", form);
        toast.success("Service created");
      } else {
        await api.put(`/services/${editing}`, form);
        toast.success("Service updated");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/services/${id}`);
    toast.success("Deleted");
    load();
  };

  const addFeature = () => {
    if (!featureInput.trim()) return;
    setForm({ ...form, features: [...form.features, featureInput.trim()] });
    setFeatureInput("");
  };
  const removeFeature = (idx) => setForm({ ...form, features: form.features.filter((_, i) => i !== idx) });

  return (
    <div className="p-8 md:p-10" data-testid="admin-services">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Content</div>
          <h1 className="font-display text-5xl uppercase text-white leading-none">Services</h1>
          <p className="text-white/60 mt-2 text-sm">Your public service offerings. Drag-less ordering via the "order" field.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="new-service-btn"><Plus size={18} weight="bold" /> New Service</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {services.map((s) => (
          <div key={s.id} className="admin-card" data-testid={`service-row-${s.slug}`}>
            <div className="aspect-[16/10] bg-atc-black border border-[#333] overflow-hidden">
              <img src={serviceImage(s)} alt="" className="w-full h-full object-cover grayscale hover:grayscale-0 transition" />
            </div>
            <div className="mt-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-heading uppercase text-white truncate">{s.title}</div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mt-1">/services/{s.slug} · order {s.order}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEdit(s)} className="border border-[#333] hover:border-atc-red p-2" data-testid={`edit-${s.slug}`}><PencilSimple size={14} weight="bold" /></button>
                <button onClick={() => del(s.id)} className="border border-[#333] hover:border-atc-red p-2" data-testid={`delete-${s.slug}`}><Trash size={14} weight="bold" /></button>
              </div>
            </div>
            <p className="text-white/60 text-xs mt-3 line-clamp-2">{s.short_description}</p>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-atc-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="service-modal">
          <div className="bg-[#1f1f1f] border-2 border-atc-red max-w-3xl w-full p-8 my-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl uppercase text-white">{editing === "new" ? "New service" : "Edit service"}</h2>
              <button onClick={close} className="text-white/60 hover:text-atc-red" data-testid="close-modal"><X size={24} weight="bold" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <div className="admin-label">Title</div>
                <input className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
              </div>
              <div>
                <div className="admin-label">Short description</div>
                <input className="admin-input" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="form-short-desc" />
              </div>
              <div>
                <div className="admin-label">Long description</div>
                <textarea rows={4} className="admin-input" value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} data-testid="form-long-desc" />
              </div>
              <ImageUploader value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} folder="services" label="Hero image" />
              <div>
                <div className="admin-label">Features / inclusions</div>
                <div className="flex gap-2">
                  <input className="admin-input flex-1" value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} placeholder="Add a feature…" data-testid="form-feature-input" />
                  <button type="button" onClick={addFeature} className="bg-atc-red text-white px-4 font-heading uppercase text-xs tracking-widest" data-testid="add-feature-btn">Add</button>
                </div>
                <ul className="mt-3 space-y-1">
                  {form.features.map((f, i) => (
                    <li key={i} className="flex items-center justify-between border border-[#333] px-3 py-2 text-white/80 text-sm">
                      <span>{f}</span>
                      <button type="button" onClick={() => removeFeature(i)} className="text-white/40 hover:text-atc-red"><X size={14} /></button>
                    </li>
                  ))}
                </ul>
              </div>
              <VideoUrlsInput
                value={form.video_urls || []}
                onChange={(arr) => setForm({ ...form, video_urls: arr })}
                label="Videos (YouTube / Vimeo / Facebook / Instagram / TikTok URLs)"
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="admin-label">Order</div>
                  <input type="number" className="admin-input" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} data-testid="form-order" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-white/80 text-sm font-heading uppercase tracking-widest">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" />
                    Featured on home
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-[#2a2a2a]">
                <button onClick={save} className="btn-primary" data-testid="save-service-btn">Save</button>
                <button onClick={close} className="btn-ghost-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
